import api from './api';

class AdminService {
  // --- Application Status Update ---
  async updateApplicationStatus(applicationId, status) {
    // Calls backend endpoint to update campaign application status
    return (await api.put(`/admin/campaign-applications/${applicationId}/status`, { status })).data;
  }
  // --- User Management ---
  async getUsers() { return (await api.get('/admin/users')).data; }
  async getUserDetails(userId) { return (await api.get(`/admin/users/${userId}`)).data; }
  async updateUser(userId, userData) { return (await api.put(`/admin/users/${userId}`, userData)).data; }
  async deleteUser(userId) { return (await api.delete(`/admin/users/${userId}`)).data; }
  async restoreUser(userId) { return (await api.post(`/admin/users/${userId}/restore`)).data; }
  async bulkRestoreUsers(userIds) { return (await api.post('/admin/users/bulk-restore', { userIds })).data; }
  async banUser(userId) { return (await api.put(`/admin/users/${userId}/ban`)).data; }
  async unbanUser(userId) { return (await api.put(`/admin/users/${userId}/unban`)).data; }
  async promoteUser(userId) { return (await api.put(`/admin/users/${userId}/promote`)).data; }
  async demoteUser(userId) { return (await api.put(`/admin/users/${userId}/demote`)).data; }
  async bulkBanUsers(userIds) { return (await api.post('/admin/users/bulk-ban', { userIds })).data; }
  async bulkDeleteUsers(userIds) { return (await api.post('/admin/users/bulk-delete', { userIds })).data; }
  async bulkGrantCoins(userIds, amount) { return (await api.post('/admin/users/bulk-grant-coins', { userIds, amount })).data; }
  async bulkNotifyUsers(userIds, message, type) { return (await api.post('/admin/users/bulk-notify', { userIds, message, type })).data; }
  async impersonateUser(userId) { return (await api.post(`/admin/users/${userId}/impersonate`)).data; }
  async resetUserPassword(userId, password) { return (await api.post(`/admin/users/${userId}/reset-password`, { password })).data; }
  async assignRoleToUser(userId, roleId) { return (await api.put(`/admin/users/${userId}/role`, { roleId })).data; }
  async notifyUser(userId, message, title, type) { return (await api.post(`/admin/users/${userId}/notify`, { message, title, type })).data; }

  // --- Export/Import Users ---
  async exportUsers() { return (await api.get('/admin/users/export')).data; }
  async importUsers(users) { return (await api.post('/admin/users/import', { users })).data; }

  // --- Campaign Management ---
  async getCampaigns() { return (await api.get('/admin/campaigns')).data; }
  async flagCampaign(campaignId) { return (await api.put(`/admin/campaigns/${campaignId}/flag`)).data; }
  async deleteCampaign(campaignId) { return (await api.delete(`/admin/campaigns/${campaignId}`)).data; }
  async bulkDeleteCampaigns(campaignIds) { return (await api.post('/admin/campaigns/bulk-delete', { campaignIds })).data; }

  // --- Coin Management ---
  async getCoinTransactions() { return (await api.get('/admin/coin-transactions')).data; }
  async grantCoins(userId, amount, reason = "Granted by admin") {
    return (await api.post(`/admin/users/${userId}/grant-coins`, { amount, reason })).data;
  }

  // --- Moderation ---
  async getFlaggedContent() { return (await api.get('/admin/moderation/flagged-content')).data; }
  async approveFlaggedContent(id) { return (await api.put(`/admin/moderation/${id}/approve`)).data; }
  async deleteFlaggedContent(id) { return (await api.delete(`/admin/moderation/${id}`)).data; }

  // --- Notification Management ---
  async getNotifications() { return (await api.get('/admin/notifications')).data; }
  async sendNotification(userIds, message, type) { return (await api.post('/admin/users/bulk-notify', { userIds, message, type })).data; }
  async scheduleNotification(notificationData) { return (await api.post('/admin/notifications/schedule', notificationData)).data; }
  async cancelScheduledNotification(id) { return (await api.put(`/admin/notifications/${id}/cancel`)).data; }

  // --- Push Notification ---
  async sendPushNotification(data) { return (await api.post('/admin/push-notification', data)).data; }

  // --- KYC Management ---
  async getKYCRequests() { return (await api.get('/admin/kyc')).data; }
  async approveKYC(id) { return (await api.put(`/admin/kyc/${id}/approve`)).data; }
  async rejectKYC(id) { return (await api.put(`/admin/kyc/${id}/reject`)).data; }

  // --- Roles & Permissions ---
  async getRoles() { return (await api.get('/admin/roles')).data; }
  async createRole(roleData) { return (await api.post('/admin/roles', roleData)).data; }
  async updateRole(roleId, roleData) { return (await api.put(`/admin/roles/${roleId}`, roleData)).data; }
  async deleteRole(roleId) { return (await api.delete(`/admin/roles/${roleId}`)).data; }

  // --- Audit Logs ---
  async getAuditLogs() { return (await api.get('/admin/logs')).data; }

  // --- Settings ---
  async getSettings() { return (await api.get('/admin/settings')).data; }
  async updateSetting(key, value) { return (await api.put('/admin/settings', { key, value })).data; }

  // --- Scheduled Jobs ---
  async getScheduledJobs() { return (await api.get('/admin/scheduled-jobs')).data; }
  async createScheduledJob(jobData) { return (await api.post('/admin/scheduled-jobs', jobData)).data; }
  async cancelScheduledJob(id) { return (await api.put(`/admin/scheduled-jobs/${id}/cancel`)).data; }

  // --- API Key Management ---
  async getApiKeys() { return (await api.get('/admin/api-keys')).data; }
  async createApiKey(apiKeyData) { return (await api.post('/admin/api-keys', apiKeyData)).data; }
  async revokeApiKey(id) { return (await api.put(`/admin/api-keys/${id}/revoke`)).data; }

  // --- Error/System Logs ---
  async getErrorLogs() { return (await api.get('/admin/error-logs')).data; }
  async clearErrorLogs() { return (await api.delete('/admin/error-logs')).data; }

  // --- Banner Control ---
  async getBanners() {
    return (await api.get('/admin/banners')).data;
  }
  
  async createBanner(bannerData) {
    return (await api.post('/admin/banners', bannerData)).data;
  }
  
  async updateBanner(bannerId, bannerData) {
    return (await api.put(`/admin/banners/${bannerId}`, bannerData)).data;
  }
  
  async deleteBanner(bannerId) {
    return (await api.delete(`/admin/banners/${bannerId}`)).data;
  }

  async uploadBannerImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    return (await api.post('/banner/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })).data;
  }

  // --- Custom Banner Control ---
  async getCustomBanners() { return (await api.get('/admin/custom-banners')).data; }
  async createCustomBanner(bannerData) { return (await api.post('/admin/custom-banners', bannerData)).data; }
  async updateCustomBanner(bannerId, bannerData) { return (await api.put(`/admin/custom-banners/${bannerId}`, bannerData)).data; }
  async deleteCustomBanner(bannerId) { return (await api.delete(`/admin/custom-banners/${bannerId}`)).data; }

  // --- FAQ Control ---
  async getFaqs() { return (await api.get('/admin/faqs')).data; }
  async createFaq(faqData) { return (await api.post('/admin/faqs', faqData)).data; }
  async updateFaq(faqId, faqData) { return (await api.put(`/admin/faqs/${faqId}`, faqData)).data; }
  async deleteFaq(faqId) { return (await api.delete(`/admin/faqs/${faqId}`)).data; }

  // --- Analytics ---
  async getAnalytics() { return (await api.get('/admin/analytics')).data; }

  // --- Payment Management ---
  async getPayments(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) params.append(key, value); });
    return (await api.get(`/admin/payments?${params.toString()}`)).data;
  }
  async getPaymentDetails(paymentId) { return (await api.get(`/admin/payments/${paymentId}`)).data; }
  async checkPaymentStatus(paymentId) { return (await api.post(`/admin/payments/${paymentId}/check-status`)).data; }
  async approvePayment(paymentId) { return (await api.put(`/admin/payments/${paymentId}/approve`)).data; }
  async rejectPayment(paymentId) { return (await api.put(`/admin/payments/${paymentId}/reject`)).data; }

  // --- Announcement Management ---
  async getAnnouncements() { return (await api.get('/admin/announcements')).data; }
  async createAnnouncement(data) { return (await api.post('/admin/announcements', data)).data; }
  async updateAnnouncement(id, data) { return (await api.put(`/admin/announcements/${id}`, data)).data; }
  async deleteAnnouncement(id) { return (await api.delete(`/admin/announcements/${id}`)).data; }
  async bulkDeleteAnnouncements(ids) { return (await api.post('/admin/announcements/bulk-delete', { ids })).data; }

  // --- Support Ticket Management (use admin.js endpoints) ---
  async getSupportTickets() {
    return (await api.get('/admin/support-tickets')).data;
  }
  async getSupportTicket(ticketId) {
    return (await api.get(`/admin/support-tickets/${ticketId}`)).data;
  }
  async updateTicketStatus(ticketId, status) {
    return (await api.put(`/admin/support-tickets/${ticketId}/status`, { status })).data;
  }
  async replyToTicket(ticketId, message) {
    return (await api.post(`/admin/support-tickets/${ticketId}/reply`, { message })).data;
  }

  // --- Debug Endpoints (optional, for testing) ---
  async debugUsersTickets() { return (await api.get('/admin/debug/users-tickets')).data; }
  async debugUsersPhones() { return (await api.get('/admin/debug/users-phones')).data; }
  async debugTicketsPhones() { return (await api.get('/admin/debug/tickets-phones')).data; }

  // --- Broadcast Notification Management ---
  async getBroadcastNotifications() {
    return (await api.get('/broadcast-notifications')).data;
  }

  async post(url, data) {
    return (await api.post(url, data)).data;
  }

  // --- Application Management ---
  async getApplications() {
    // This endpoint should match your backend route for listing campaign applications for admin/brand
    // If you want all applications, use: /admin/campaign-applications
    // If you want brand's applications, use: /admin/campaigns/:id/applications or similar
    return (await api.get('/admin/campaign-applications')).data;
  }

  // --- Reports Generation ---
  async generateReport(type, dateRange = '30d', format = 'csv') {
    return (await api.post('/admin/reports/generate', { type, dateRange, format })).data;
  }

  // --- Collaboration Management ---
  async getCollaborations() { return (await api.get('/admin/collaborations')).data; }
  async getContactRequests() { return (await api.get('/admin/contact-requests')).data; }
  async getContactRequestFee() { return (await api.get('/admin/contact-request-fee')).data; }
  async updateContactRequestFee(fee) { return (await api.put('/admin/contact-request-fee', { fee })).data; }
  async getRatings() { return (await api.get('/admin/ratings')).data; }
  async flagRating(ratingId) { return (await api.put(`/admin/ratings/${ratingId}/flag`)).data; }
  async deleteRating(ratingId) { return (await api.delete(`/admin/ratings/${ratingId}`)).data; }

  // --- Tutorial Video Management ---
  async getTutorialVideos() { return (await api.get('/admin/tutorial-videos')).data; }
  async createTutorialVideo(videoData) { return (await api.post('/admin/tutorial-videos', videoData)).data; }
  async updateTutorialVideo(videoId, videoData) { return (await api.put(`/admin/tutorial-videos/${videoId}`, videoData)).data; }
  async deleteTutorialVideo(videoId) { return (await api.delete(`/admin/tutorial-videos/${videoId}`)).data; }
  async reorderTutorialVideos(orderData) { return (await api.put('/admin/tutorial-videos/reorder', { videoOrders: orderData })).data; }
}

export const adminService = new AdminService();
